/*
 * Planner Daily Schedule — backed by ONE backend call.
 *
 * GET /api/v2/views/schedule returns the fully-composed schedule (EHR cached
 * appointments + patient cache, Recall scheduled bots, and the caller's
 * recent sessions grouped into encounter views — scoped, matched, deduped
 * and bucketed server-side; see calliope-backend
 * src/services/views/scheduleView.ts). This hook keeps only what the client
 * owns:
 *   - provider-selection persistence (localStorage → providerKeys/allProviders
 *     query params; the server treats a manual selection as authoritative);
 *   - presentation mapping (plannerScheduleView: card time labels in the
 *     viewer's display timezone, labels/accents, legacy raw-source
 *     stand-ins for the PlannerEvent action handlers);
 *   - the snapshot cache / manual-refresh cadence.
 *
 * The old client composition (plannerScheduleAdapter.buildPlannerSchedule +
 * plannerEncounterHydration + useScheduledBots + the appointments/patients
 * fan-out) is retired from this live path. plannerScheduleAdapter stays only
 * for its exported pure helpers (tests + scaffold rendering).
 *
 * The encounter-history seed is NOT schedule data: it feeds the browser's
 * Completed/Unlinked tabs' first page and the continue-session banner
 * (PlannerBrowserColumn), which page through /sessions/recent independently
 * of the composed schedule. It stays a separate fetchUserEncounterList call
 * — without the schedule-oriented hydration pass (history rows hydrate
 * per-row via useHydratedPlannerPatientEvents when rendered).
 */

import {useCallback, useEffect, useMemo, useState} from 'react';
import {fetchUserEncounterList} from '../../services/encounterService';
import {fetchScheduleView} from '../../services/viewsClient';
import type {ScheduleViewResponse} from '../../services/viewsClient';
import {EMPTY_PLANNER_SCHEDULE} from './plannerScheduleAdapter';
import {getSystemTimeZone} from './plannerDateTime';
import {
  mapScheduleProviderScope,
  mapScheduleViewSections,
} from './plannerScheduleView';
import {
  isSelectionActive,
  loadStoredSelection,
  storeSelection,
  type ScheduleScopeSelection,
} from './plannerProviderScope';
import {
  getMockEhrSchedule,
  getMockEhrRecentEncounters,
  isMockEhrEnabled,
} from '../../mocks/mockEhrFixtures';

const EMPTY_STATE: Record<string, any> = {
  scheduleData: null as ScheduleViewResponse | null,
  encounterHistorySeed: null,
  timeZone: 'America/New_York',
  isLoading: false,
  error: null,
  hasLoaded: false,
};

// Snapshot cache mirroring the planner's other module caches (history pages,
// phone calls): a fully-successful load is kept for a few minutes so
// re-entering the planner paints the last schedule instantly instead of
// cold-starting on a spinner. Stale-while-revalidate: a cached snapshot is
// only the first paint — every mount still goes to the network and overwrites
// it, so a session completed seconds before returning to the planner shows up
// on that return, not after the TTL. Manual refresh (reloadToken) bypasses
// the cached paint entirely.
const SNAPSHOT_CACHE = new Map<string, Record<string, any>>();
const SNAPSHOT_CACHE_TTL_MS = 5 * 60 * 1000;
const SNAPSHOT_CACHE_LIMIT = 5;

export const clearPlannerScheduleSnapshotCache = () => {
  SNAPSHOT_CACHE.clear();
};

const selectionCacheKey = (selection: ScheduleScopeSelection | null) => {
  if (!isSelectionActive(selection)) return 'auto';
  if (selection!.all) return 'all';
  return [...selection!.keys].sort().join(',');
};

const snapshotCacheKey = (
  userId: any,
  companyId: any,
  pageSize: any,
  selection: ScheduleScopeSelection | null,
) =>
  `${userId || ''}|${companyId || ''}|${pageSize || ''}|${selectionCacheKey(selection)}`;

const readScheduleSnapshot = (key: string) => {
  const cached = SNAPSHOT_CACHE.get(key);
  if (!cached) return null;
  if (Date.now() - (cached.cachedAt || 0) >= SNAPSHOT_CACHE_TTL_MS) {
    SNAPSHOT_CACHE.delete(key);
    return null;
  }
  return cached;
};

const writeScheduleSnapshot = (key: string, snapshot: Record<string, any>) => {
  if (!key) return;
  if (SNAPSHOT_CACHE.has(key)) SNAPSHOT_CACHE.delete(key);
  SNAPSHOT_CACHE.set(key, {...snapshot, cachedAt: Date.now()});
  while (SNAPSHOT_CACHE.size > SNAPSHOT_CACHE_LIMIT) {
    const oldestKey = SNAPSHOT_CACHE.keys().next().value;
    if (!oldestKey) break;
    SNAPSHOT_CACHE.delete(oldestKey);
  }
};

function combineErrorMessages(...messages: any[]) {
  const unique = [...new Set(messages.filter(Boolean).map(String))];
  return unique.length ? unique.join(' ') : null;
}

function buildEncounterHistorySeed({
  encounters = [],
  result = {},
  reloadToken,
  status = 'raw',
  userId,
}: Record<string, any>) {
  return {
    encounters,
    lastDoc: result?.lastDoc || null,
    hasMore: Boolean(result?.hasMore),
    hasLoaded: true,
    key: `${userId}:${reloadToken}:${status}:${encounters.length}:${
      result?.lastDoc?.id || ''
    }`,
  };
}

export default function usePlannerSchedule({
  userData,
  enabled = true,
  pageSize = 80,
}: Record<string, any> = {}) {
  const userId = userData?.userId || null;
  const companyId = userData?.companyId || null;
  const isMockEhrMode = isMockEhrEnabled(userData);

  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState(EMPTY_STATE);
  const displayTimeZone = useMemo(() => getSystemTimeZone(), []);

  // Manual provider scope: which attending(s)' schedule the user chose to view
  // when we couldn't auto-match them to their own appointments. Persisted per
  // user so a resident who rounds with the same attending doesn't re-pick.
  // The stored selection is passed to the backend as providerKeys /
  // allProviders — scoping itself runs server-side.
  const [scopeSelection, setScopeSelection] =
    useState<ScheduleScopeSelection | null>(() => loadStoredSelection(userId));

  useEffect(() => {
    setScopeSelection(loadStoredSelection(userId));
  }, [userId]);

  const updateScopeSelection = useCallback(
    (next: ScheduleScopeSelection | null) => {
      const normalized = isSelectionActive(next) ? next : null;
      setScopeSelection(normalized);
      storeSelection(userId, normalized);
    },
    [userId],
  );

  const clearScopeSelection = useCallback(() => {
    setScopeSelection(null);
    storeSelection(userId, null);
  }, [userId]);

  useEffect(() => {
    if (!enabled || !userId) {
      setState(EMPTY_STATE);
      return undefined;
    }

    if (isMockEhrMode) {
      const scheduleData = getMockEhrSchedule(new Date(), scopeSelection);
      setState({
        scheduleData,
        encounterHistorySeed: buildEncounterHistorySeed({
          encounters: getMockEhrRecentEncounters(),
          result: {lastDoc: null, hasMore: false},
          reloadToken,
          status: 'mock',
          userId,
        }),
        timeZone: scheduleData.timeZone,
        isLoading: false,
        error: null,
        hasLoaded: true,
      });
      return undefined;
    }

    // Stale-while-revalidate: paint a fresh-enough snapshot instantly on
    // plain mounts, then continue to the network below and overwrite it.
    // The cached paint only suppresses the loading state — skipping the
    // fetch here is what made a just-completed session invisible on the
    // Recent tab until the snapshot TTL lapsed.
    const cacheKey = snapshotCacheKey(
      userId,
      companyId,
      pageSize,
      scopeSelection,
    );
    const cached = reloadToken === 0 ? readScheduleSnapshot(cacheKey) : null;
    if (cached) {
      setState({
        scheduleData: cached.scheduleData,
        encounterHistorySeed: cached.encounterHistorySeed,
        timeZone: cached.timeZone,
        isLoading: false,
        error: null,
        hasLoaded: true,
      });
    }

    let cancelled = false;
    if (!cached) {
      setState(prev => ({...prev, isLoading: true, error: null}));
    }

    (async () => {
      const errors: any[] = [];

      // ONE schedule call. Manual selection rides as query params; the
      // server otherwise auto-scopes (or flags needs-selection).
      const loadSchedule = async (): Promise<ScheduleViewResponse | null> => {
        try {
          const manualActive = isSelectionActive(scopeSelection);
          return await fetchScheduleView({
            tz: displayTimeZone,
            providerKeys:
              manualActive && !scopeSelection!.all
                ? scopeSelection!.keys
                : null,
            allProviders: Boolean(manualActive && scopeSelection!.all),
          });
        } catch (err) {
          errors.push('Could not load your schedule.');
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[PlannerSchedule] schedule view load failed:',
              (err as any)?.message || err,
            );
          }
          return null;
        }
      };

      // Encounter-history seed for the browser tabs + continue banner (see
      // header note) — deliberately separate from the composed schedule.
      const loadEncounters = async () => {
        try {
          const result = await fetchUserEncounterList(userId, {pageSize});
          const encounters = result?.encounters || [];
          return {
            encounterHistorySeed: buildEncounterHistorySeed({
              encounters,
              result,
              reloadToken,
              status: 'raw',
              userId,
            }),
          };
        } catch (err) {
          errors.push('Could not load encounters.');
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[PlannerSchedule] encounter load failed:',
              (err as any)?.message || err,
            );
          }
          return {
            encounterHistorySeed: buildEncounterHistorySeed({
              encounters: [],
              result: {lastDoc: null, hasMore: false},
              reloadToken,
              status: 'error',
              userId,
            }),
          };
        }
      };

      const [scheduleData, {encounterHistorySeed}] = await Promise.all([
        loadSchedule(),
        loadEncounters(),
      ]);

      if (cancelled) return;
      // A failed revalidate behind a served snapshot keeps the snapshot —
      // replacing painted data with an error-shaped blank (or an error
      // banner) for a background refresh would be strictly worse than the
      // slightly-stale view. The next mount or manual refresh retries.
      if (errors.length && cached) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[PlannerSchedule] background revalidate failed; keeping cached snapshot:',
            ...errors,
          );
        }
        return;
      }
      const snapshot = {
        scheduleData,
        encounterHistorySeed,
        timeZone: scheduleData?.timeZone || 'America/New_York',
      };
      setState({
        ...snapshot,
        isLoading: false,
        error: combineErrorMessages(...errors),
        hasLoaded: true,
      });
      // Only clean loads are worth replaying on the next mount; a partial
      // failure should retry rather than pin its error-shaped data for 5 min.
      if (!errors.length) {
        writeScheduleSnapshot(cacheKey, snapshot);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    companyId,
    displayTimeZone,
    enabled,
    pageSize,
    reloadToken,
    scopeSelection,
    userId,
    isMockEhrMode,
  ]);

  const refresh = useCallback(() => {
    setReloadToken(value => value + 1);
  }, []);

  const schedule = useMemo(() => {
    if (!enabled || !userId || !state.scheduleData) {
      return EMPTY_PLANNER_SCHEDULE;
    }
    return mapScheduleViewSections(state.scheduleData, {displayTimeZone});
  }, [displayTimeZone, enabled, state.scheduleData, userId]);

  const scheduleScope = useMemo(() => {
    const scopeInfo = mapScheduleProviderScope(state.scheduleData);
    const appointmentCount = Object.values(schedule).reduce(
      (sum: number, items: any) =>
        sum +
        (Array.isArray(items)
          ? items.filter((item: any) => item.sourceType === 'ehrAppointment')
              .length
          : 0),
      0,
    );
    return {
      // Whether the user must pick a schedule (auto match found nothing and
      // they have not chosen yet) — drives the picker prompt vs the list.
      needsManualSelection: scopeInfo.needsManualSelection,
      autoMatched: scopeInfo.autoMatched,
      availableProviders: scopeInfo.availableProviders,
      selection: scopeSelection,
      isAll: Boolean(scopeSelection?.all),
      selectedKeys: scopeSelection?.keys || [],
      isManual: isSelectionActive(scopeSelection),
      totalAppointments: scopeInfo.totalAppointments,
      scopedCount: appointmentCount,
      setSelection: updateScopeSelection,
      clearSelection: clearScopeSelection,
    };
  }, [
    clearScopeSelection,
    schedule,
    scopeSelection,
    state.scheduleData,
    updateScopeSelection,
  ]);

  return {
    schedule,
    scheduleScope,
    encounterHistorySeed: state.encounterHistorySeed,
    isLoading: state.isLoading,
    error: state.error,
    refresh,
    hasLoaded: state.hasLoaded,
  };
}
